import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Dialog,
  // DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Container,
  Grid,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import styles from './style';
import ModalTitleWithCloseIcon from '../modalTitleWithCloseIcon';
import { updateTempState } from '../../../actions/temp';

const useStyles = makeStyles(styles);

const AlertDialog = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery('(min-width:600px)');

  // Custom Properties
  const {
    title,
    description,
    content,
    dismissiveAction,
    confirmingAction,
    acknowledgementAction,
    onClose,
    open: initialOpen = true,
    isBlocking,
    fullWidth,
    maxWidth = 'sm',
    allowFullscreen = true,
    noDefaultDismissiveAction,
    dontResetGlobalAlertModal,
    hideXButton,
  } = props;

  const [open, setOpen] = useState(initialOpen);
  const [dismissiveActionLoading, setDismissiveActionLoading] = useState(false);
  const lastBrowserUrl = useSelector((state) => state.temp.lastBrowserUrl);
  const isMountedRef = useRef(true);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const neutralizeBack = (callback) => {
    if (lastBrowserUrl !== window.location.href) {
      window.history.pushState(null, '', window.location.href);
      dispatch(
        updateTempState.updateTempStateAction({ lastBrowserUrl: window.location.href }),
      );
    }
    window.onpopstate = () => {
      // window.history.pushState(null, '', window.location.href);
      callback();
    };
  };

  const revivalBack = () => {
    window.onpopstate = undefined;
    window.history.go(-1);
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
    if (!dontResetGlobalAlertModal)
      dispatch(updateTempState.updateTempStateAction({ alertPopUp: null }));
  };

  const handleBeforeClose = () => {
    handleClose();
    revivalBack();
  };

  useEffect(() => {
    if (open && !hideXButton) {
      neutralizeBack(handleClose);
    }
  }, [open]);

  const defaultCloseDismissiveAction = (
    <Button
      className={classes.secondaryButton}
      sx={{ width: '6rem', mx: 1 }}
      onClick={() => handleBeforeClose()}
    >
      Close
    </Button>
  );

  const finalDismissiveAction =
    !dismissiveAction && !noDefaultDismissiveAction
      ? defaultCloseDismissiveAction
      : dismissiveAction;

  // Added: Extends dismissiveAction to accept string or { label, onClick, disabled } object
  // in addition to React elements. This allows callers to pass simple config objects
  // instead of building full JSX, with consistent branded button styling across all modals.
  const renderDismissiveAction = (action) => {
    if (!action) return null;
    if (React.isValidElement(action)) return action;
    const label =
      typeof action === 'string'
        ? action
        : typeof action === 'object'
          ? action?.label
          : null;
    if (!label) return action;
    const onClick =
      typeof action === 'object' && action?.onClick
        ? action.onClick
        : () => handleBeforeClose();
    const isDisabled =
      typeof action === 'object' && action?.disabled ? action.disabled : false;
    return (
      <Button
        className={classes.submitButton}
        variant="contained"
        color="primary"
        disableElevation
        disabled={isDisabled}
        sx={{
          borderRadius: '8px',
          backgroundColor: '#31006F',
          color: '#FFFFFF',
          fontSize: '16px',
          fontWeight: 600,
          textTransform: 'none',
          minWidth: '140px',
          px: 3,
          py: 1,
          '&:hover': {
            backgroundColor: '#31006F',
          },
        }}
        onClick={onClick}
      >
        {label}
      </Button>
    );
  };

  return (
    <Dialog
      fullScreen={allowFullscreen ? !isDesktop : false}
      onClose={() => {
        if (!isBlocking) {
          handleBeforeClose();
        }
      }}
      open={open}
      classes={{
        paper: classes.dialogContainer,
      }}
    >
      <Box sx={{ px: 2 }} className={classes.dialogContent}>
        <ModalTitleWithCloseIcon
          className={classes.headerTitle}
          classes={{ root: classes.dialogActions }}
          title={title}
          onClose={!hideXButton && handleBeforeClose}
        />
      </Box>
      <DialogContent className={classes.dialogContent}>
        <Container maxWidth={maxWidth}>
          <Grid container spacing={1}>
            {description && (
              <Grid item xs={12}>
                <Typography sx={{ color: 'text.label' }} variant="body1">
                  {description}
                </Typography>
              </Grid>
            )}
            {content && (
              <Grid item xs={12}>
                {content}
              </Grid>
            )}
          </Grid>
        </Container>
      </DialogContent>

      {(finalDismissiveAction || confirmingAction || acknowledgementAction) && (
        <DialogActions className={classes.dialogActions}>
          {/* Changed: Wrap dismissiveAction through renderDismissiveAction to support string/object action types */}
          {renderDismissiveAction(finalDismissiveAction)}
          {confirmingAction}
          {acknowledgementAction}
        </DialogActions>
      )}
    </Dialog>
  );
};

AlertDialog.defaultProps = {
  onClose: () => {},
};

AlertDialog.propTypes = {
  // Dialog Properties
  onClose: PropTypes.func,

  // Custom Properties
  title: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  content: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  dismissiveAction: PropTypes.element,
  confirmingAction: PropTypes.element,
  acknowledgementAction: PropTypes.element,
};

export default AlertDialog;

/*
  dialogProps: {
    open: true,
    onClose: () => {},
  },
  */
